import { useEffect, useRef, useState } from "react";
import { getDayDifference, getUserName } from "../../../../helpers";
import { DeleteBin, Heart, More, Siren } from "../../../../icons";
import ProfileImageComp from "../../../common/profile";
import { useAddFeedReactionMutation } from "../../../../store/api/homeApi";
import { toastError } from "../../../toast";
import ProfileBadgeAvatar from "../../../user/user-profile/profile-avatar";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../constants/routes";
import { useAppDispatch } from "../../../../hooks/store-hooks";
import { getLocalStorage } from "../../../../helpers/storage";
import { openModal } from "../../../../store/slices/modalSlice";
import { MODAL_NAME } from "../../../../constants";

const ReplyComment = ({
  postId = "",
  replyData = {},
  handleReplyClick,
}: any) => {
  const dispatch = useAppDispatch();
  const user = JSON.parse(getLocalStorage("userData") || "{}");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [moreOptions, setmoreOptions] = useState(false);
  const [addFeedReaction] = useAddFeedReactionMutation();

  const {
    id: replyId,
    user: replyUser,
    reaction_message,
    created_at,
    like_count,
    liked,
  } = replyData || {};

  const [likeData, setLikeData] = useState({
    like: false,
    count: 0,
  });

  useEffect(() => {
    setLikeData({
      ...likeData,
      like: liked,
      count: like_count,
    });
  }, [like_count, liked]);

  const handleLike = async () => {
    try {
      const response = await addFeedReaction({
        activityId: postId,
        bodyData: { type: "like", parent_id: replyId },
      }).unwrap();
      if (response.success) {
        setLikeData({
          ...likeData,
          like: !likeData.like,
          count: likeData.like ? likeData.count - 1 : likeData.count + 1,
        });
      }
    } catch (error) {
      toastError({ message: error });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setmoreOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [moreOptions]);
  return (
    <div key={replyId} className="mt-4 ml-12 pl-4">
      <div className="flex items-start mb-2">
        <Link to={ROUTES.USER_PROFILE.replace(":user_id", replyUser?.id)}>
          {replyUser?.badge ? (
            <div className={`relative w-10 h-[40px] mr-2`}>
              <ProfileBadgeAvatar
                profileImage={replyUser?.profile_picture}
                customDivClassName="absolute h-10 w-10 border-4 z-1"
                customImgClassName="h-[40px] w-[40px] absolute rounded-full p-[3px]"
                customIconDivClassName="absolute z-[120] -bottom-[5px] left-[15px]"
                customStyle1="w-[50px] h-[14px] absolute -bottom-2 z-[100] overflow-hidden"
                customStyle2="w-[200px] h-4 absolute top-3 z-10 -left-10 overflow-hidden"
                customStarWidth={10}
                customStarHeight={10}
                customStyle3="w-[34px] h-[34px]"
                badge={replyUser?.badge}
              />
            </div>
          ) : (
            <ProfileImageComp
              imageUrl={replyUser?.profile_picture}
              customClass="w-10 h-10"
              is_opc={replyUser?.memberships?.is_opc}
            />
          )}
        </Link>
        <div className="flex-1 rounded-xl-10 bg-primary-300 p-3">
          <div className="flex items-center justify-between">
            <Link to={ROUTES.USER_PROFILE.replace(":user_id", replyUser?.id)}>
              <p className="text-sm font-semibold">{getUserName(replyUser)}</p>
              <p className="text-xs text-gray-500">{replyUser?.designation}</p>
            </Link>
            <div className="flex flex-row items-center gap-3 relative">
              <p className="text-xs text-gray-500">
                {getDayDifference(created_at)}
              </p>
              <div
                className="hover:cursor-pointer"
                onClick={() => {
                  setmoreOptions(true);
                }}
              >
                <More />
              </div>
              {replyUser?.id === user?._id ? (
                <div
                  ref={wrapperRef}
                  className={`absolute z-20 right-0 flex flex-col items-start justify-start bg-white shadow top-8 w-52 rounded-xl ${moreOptions ? "" : "hidden"}`}
                >
                  <button
                    type="button"
                    className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
                    onClick={() => {
                      dispatch(
                        openModal({
                          modalName: MODAL_NAME.DELETE_REACTION,
                          modalData: {
                            entityId: replyId,
                            reactionType: "reply",
                            postId: postId,
                          },
                        }),
                      );
                    }}
                  >
                    <DeleteBin width={18} height={18} />
                    <div className="text-base font-normal leading-tight text-black">
                      Delete Reply
                    </div>
                  </button>
                </div>
              ) : (
                <div
                  ref={wrapperRef}
                  className={`absolute z-20 right-0 flex flex-col items-start justify-start bg-white shadow top-5 w-52 rounded-xl ${moreOptions ? "" : "hidden"}`}
                >
                  <button
                    onClick={() => {
                      dispatch(
                        openModal({
                          modalName: MODAL_NAME.REPORT_REACTION,
                          modalData: {
                            entityId: replyId,
                            reactionType: "reply",
                          },
                        }),
                      );
                    }}
                    type="button"
                    className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
                  >
                    <Siren />
                    <div className="text-base font-normal leading-tight text-black">
                      Report Reply
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
          <p className="text-sm mt-2">{reaction_message}</p>
        </div>
      </div>
      <div className="flex items-center space-x-4 text-gray-500 text-sm !ml-12">
        <div className="flex items-center space-x-2 cursor-pointer">
          <div onClick={handleLike} className="flex">
            <Heart
              fillColor={likeData?.like ? "#FF5147" : "#ffffff"}
              strokeColor={likeData?.like ? "#FF5147" : "#343330"}
            />
            <span className="ml-1">{likeData?.count || 0}</span>
          </div>
          <div> | </div>
          <button onClick={handleReplyClick}>Reply</button>
        </div>
      </div>
    </div>
  );
};

export default ReplyComment;
